import { useState, useEffect } from "react";
import { Check, Clock, X } from "lucide-react";
import { PickupPicker, defaultPickupDate, WINDOWS } from "../components/PickupPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuotePage() {
  const token = window.location.pathname.split("/quote/")[1] || "";
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pickupDate, setPickupDate] = useState(defaultPickupDate());
  const [pickupWindow, setPickupWindow] = useState(WINDOWS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [tipPct, setTipPct] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/quote/${token}`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setQuote(data);
        if (data.booked) setDone(true);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    setSubmitting(true);
    try {
      if (payMethod === "card") {
        const res = await fetch(`${API}/payments/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            origin_url: window.location.origin,
            quote_token: token,
            pickup_date: pickupDate,
            pickup_window: pickupWindow,
            tip_pct: tipPct,
          }),
        });
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
        throw new Error("checkout failed");
      }
      const res = await fetch(`${API}/quote/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pickup_date: pickupDate, pickup_window: pickupWindow, payment_method: "cash", base_url: window.location.origin }),
      });
      if (res.ok) setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="grain flex min-h-screen items-center justify-center bg-canvas px-5 py-12">
      <div className="w-full max-w-lg" data-testid="quote-page">{children}</div>
    </div>
  );

  if (loading) return <Shell><p className="text-center text-charcoal/45">Loading your quote…</p></Shell>;

  if (notFound) return (
    <Shell>
      <div className="rounded-[2rem] bg-white/70 p-9 text-center" data-testid="quote-notfound">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-stone/60 text-charcoal"><X size={24} /></span>
        <h1 className="mt-5 font-display text-3xl text-charcoal">Quote not found</h1>
        <p className="mt-2 text-charcoal/55">This link may be incorrect. Reach out at 617-717-9852 and we'll help.</p>
      </div>
    </Shell>
  );

  if (done) return (
    <Shell>
      <div className="rounded-[2rem] bg-white/70 p-9 text-center" data-testid="quote-booked">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage text-white"><Check size={32} /></span>
        <h1 className="mt-6 font-display text-3xl text-charcoal sm:text-4xl">You're all set{quote?.name ? `, ${quote.name.split(" ")[0]}` : ""}.</h1>
        <p className="mt-3 text-charcoal/60">Your pickup is booked. A friendly crew will reach out shortly to confirm your window. See you soon!</p>
        <p className="mt-6 font-display text-xl text-violet">{quote?.price}</p>
      </div>
    </Shell>
  );

  if (quote?.expired) return (
    <Shell>
      <div className="rounded-[2rem] bg-white/70 p-9 text-center" data-testid="quote-expired">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-stone/60 text-charcoal"><Clock size={24} /></span>
        <h1 className="mt-5 font-display text-3xl text-charcoal">This quote has expired</h1>
        <p className="mt-2 text-charcoal/55">No worries — call us at 617-717-9852 or start a fresh quote and we'll get you a new price.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="overflow-hidden rounded-[2rem] bg-white/70 shadow-[0_40px_120px_-40px_rgba(23,23,23,0.4)]">
        <div className="bg-violet px-8 py-6">
          <span className="font-logo font-semibold text-3xl lowercase text-white">milla</span>
        </div>
        <div className="px-7 py-8 sm:px-9">
          <h1 className="font-display text-3xl tracking-tight text-charcoal sm:text-4xl">
            Hi{quote.name ? ` ${quote.name.split(" ")[0]}` : ""}, your quote is ready.
          </h1>

          <div className="mt-6 flex items-end justify-between rounded-[1.6rem] bg-lavender/50 px-7 py-6">
            <div>
              <p className="text-sm text-charcoal/55">Your pickup price</p>
              <p className="font-display text-5xl tracking-tight text-violet" data-testid="quote-price">{quote.price}</p>
            </div>
            <span className="rounded-full bg-white/70 px-3 py-1.5 text-xs font-semibold text-charcoal/60">Valid until {quote.expires_at}</span>
          </div>

          {quote.note && (
            <div className="mt-5 rounded-2xl bg-canvas/70 px-5 py-4 text-charcoal/75" data-testid="quote-note">{quote.note}</div>
          )}

          <div className="mt-6 rounded-[1.6rem] bg-canvas/60 p-6">
            <p className="text-sm font-medium text-charcoal/70">Every pickup includes</p>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
              {quote.includes.map((inc) => (
                <li key={inc} className="flex items-center gap-2 text-sm text-charcoal">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sage/50"><Check size={12} /></span>{inc}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-7">
            <p className="text-sm font-medium text-charcoal/70">Choose a pickup day &amp; window</p>
            <div className="mt-3">
              <PickupPicker date={pickupDate} window={pickupWindow} onDate={setPickupDate} onWindow={setPickupWindow} accent="sage" />
            </div>
          </div>

          {/* payment method */}
          <div className="mt-6" data-testid="quote-payment-method">
            <p className="text-sm font-medium text-charcoal/70">How would you like to pay?</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button onClick={() => setPayMethod("cash")} data-testid="quote-pay-cash"
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${payMethod === "cash" ? "border-violet bg-lavender/40" : "border-stone bg-white/70 hover:border-violet/40"}`}>
                <p className="text-sm font-semibold text-charcoal">💵 Cash on pickup</p>
                <p className="mt-0.5 text-xs text-charcoal/50">Pay the crew on the day.</p>
              </button>
              <button onClick={() => setPayMethod("card")} data-testid="quote-pay-card"
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${payMethod === "card" ? "border-violet bg-lavender/40" : "border-stone bg-white/70 hover:border-violet/40"}`}>
                <p className="text-sm font-semibold text-charcoal">💳 Pay by card</p>
                <p className="mt-0.5 text-xs text-charcoal/50">Secure checkout, pay now.</p>
              </button>
            </div>
            {payMethod === "card" && (
              <div className="mt-4" data-testid="quote-tip-section">
                <p className="text-sm font-medium text-charcoal/70">Add a tip for the crew? <span className="text-charcoal/40">(optional)</span></p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[0, 10, 15, 20].map((t) => (
                    <button key={t} onClick={() => setTipPct(t)} data-testid={`quote-tip-${t}`}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${tipPct === t ? "bg-sage/60 text-charcoal border border-sage" : "bg-white/70 text-charcoal/65 border border-stone hover:border-sage"}`}>
                      {t === 0 ? "No tip" : `${t}%`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button onClick={accept} disabled={submitting} data-testid="quote-accept-btn"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all enabled:hover:bg-violet-deep enabled:hover:-translate-y-0.5 disabled:opacity-60">
            {submitting ? "Booking…" : payMethod === "card" ? "Continue to payment" : "Accept & book my pickup"}
          </button>
          <p className="mt-3 text-center text-xs text-charcoal/45">Final price confirmed on-site. Questions? Call 617-717-9852.</p>
        </div>
      </div>
    </Shell>
  );
}
